import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useJsonLd } from "@/hooks/useJsonLd";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容定位",
    desc: "只做国产影视，把电影、电视剧、年代剧与类型片分开整理，让选择更明确。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "信息结构",
    desc: "统一的卡片与详情字段，片名、年份、类型、主创、剧情梗概一处看全。",
  },
  {
    icon: "ri-heart-3-line",
    title: "使用体验",
    desc: "移动端与桌面端都做了适配，筛选、搜索与浏览都能在几步之内完成。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮件联系", value: "service@guochanjingpin99.demo" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "国产精品久久" },
  { icon: "ri-message-3-line", label: "内容反馈", value: "feedback@guochanjingpin99.demo" },
  { icon: "ri-time-line", label: "更新时段", value: "每日 10:00 / 20:00" },
];

const faqs = [
  {
    q: "网站可以观看影视剧吗？",
    a: "不可以。本站是影视内容的信息索引与展示平台，只提供片名、年份、类型、主创与剧情简介等信息，不提供播放、下载或任何付费服务。",
  },
  {
    q: "页面上的影视作品是真实存在的吗？",
    a: "本站为演示站点，部分作品名称与剧情为虚构示例，评分与热度也都是模拟数据，仅用于展示页面结构与交互效果。",
  },
  {
    q: "如何按类型快速找到想看的作品？",
    a: "可以从首页的分类快捷入口进入分类页，也可以在分类页左侧筛选栏组合「电影/电视剧 + 类型 + 年份」三个条件，结果会在页面内即时刷新。",
  },
  {
    q: "分享按钮会把页面发到社交平台吗？",
    a: "不会。分享功能为前端演示，只会复制当前页面链接到剪贴板，不会向任何第三方平台发送内容。",
  },
];

export default function AboutPage() {
  useJsonLd("about-faq-jsonld", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1 pt-16">
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto w-full max-w-6xl">
            <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-900">关于我们</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              关于国产精品久久
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
              一个专注于国产电影与电视剧的内容聚合平台。我们相信，好的国产影视作品值得被更多人找到。
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-primary-600" />
                我们做什么
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700">
                <p>
                  国产精品久久成立于一个很朴素的想法：国产影视作品数量越来越多，但找起来依然麻烦。
                  有的平台只按热度推荐，有的平台缺少完整的作品资料，观众常常要跨好几个平台才能确认一部剧的导演、年份和剧情。
                </p>
                <p>
                  于是我们把作品信息整理成一张可以检索的索引表——按影视形式（电影 / 电视剧）、
                  类型（剧情、悬疑、科幻、古装、家庭、年代等）与上映年份建立维度，
                  再配合关键词搜索，让你在几十秒内从「想看点什么」走到「就看这一部」。
                </p>
                <p>
                  网站内容目前为虚构演示数据，用于呈现完整的产品形态与交互流程。
                  我们不提供播放、下载、登录与支付功能，也不收集你的个人观影记录。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {values.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <h3 className="mt-3 text-sm font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                常见问题
              </h2>
              <div className="mt-4 divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {faqs.map((faq, index) => (
                  <details key={faq.q} className="group px-4 py-4 md:px-5" open={index === 0}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                      {faq.q}
                      <i className="ri-add-line text-foreground-500 transition-transform group-open:rotate-45" />
                    </summary>
                    <p className="mt-2.5 text-xs leading-relaxed text-foreground-600 md:text-sm">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>

            <div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                <h2 className="font-heading text-lg font-black text-foreground-950">联系我们</h2>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                  以下为演示联系方式，如对内容有建议或发现信息有误，欢迎反馈。
                </p>
                <div className="mt-4 flex flex-col gap-2.5">
                  {contacts.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 rounded-md bg-background-50 px-3 py-2.5"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                        <i className={`${item.icon} text-sm`} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] text-foreground-500">{item.label}</p>
                        <p className="truncate text-xs text-foreground-900">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="font-heading text-lg font-black text-foreground-950">版权声明</h2>
                <div className="mt-2.5 space-y-2.5 text-xs leading-relaxed text-foreground-600">
                  <p>
                    本站所有影视作品的名称、海报与简介均为虚构演示内容，仅用于展示页面结构，不指向任何真实作品或权利方。
                  </p>
                  <p>
                    本站不存储、不传播任何影视音视频文件，不提供播放与下载入口。如本站内容涉及第三方权益，请通过上方邮箱联系我们处理。
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="font-heading text-lg font-black text-foreground-950">服务条款</h2>
                <div className="mt-2.5 space-y-2.5 text-xs leading-relaxed text-foreground-600">
                  <p>1. 本站内容仅供信息浏览与参考，不构成任何观看、购买或授权建议。</p>
                  <p>2. 用户使用本站产生的任何行为应遵守当地法律法规，不得用于商业转载。</p>
                  <p>3. 本站有权根据运营需要调整页面内容与数据结构，恕不另行通知。</p>
                </div>
              </div>

              <Link
                to="/category"
                className="mt-4 flex items-center justify-between rounded-lg bg-primary-600 px-4 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
              >
                去分类页逛逛
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}